import React, { createContext, Suspense, useContext, useState } from 'react';
import { Box, IconButton, List, ListItemButton, Tooltip } from '@mui/material';
import { useCurrentuser } from '@uplift/contexts';
import { trackVideoSidebarButtonClick } from '@uplift/hooks';
import { ProviderMedicalRole } from '@uplift/types';
import { CollapseWidth, UpliftThemeProvider, AlertProvider } from '@uplift/ui-v2';

import { useVideoCallLayoutStyles } from './styles';
import { withStyles } from 'tss-react/mui';

const VideoCallSidebarContext = createContext<{
  activeItemId: string | null;
  setActiveItemId: (i: string | null) => void;
} | null>(null);

export const VideoCallSidebarProvider: React.FC = ({ children }) => {
  const [ activeItemId, setActiveItemId ] = useState<string | null>(null);

  return (
    <VideoCallSidebarContext.Provider
      value={{ activeItemId, setActiveItemId }}
      children={children}
    />
  );
};


export const VideoCallSidebarAlertProvider: React.FC<{ contentWidth: string }> = ({ children, contentWidth }) => {
  return (
    <AlertProvider
      containerPlacement={{
        right: '64px',
        top: '48px',
        width: contentWidth
      }}
    >
      {children}
    </AlertProvider>
  );
};


export const useVideoCallSidebarContext = () => {
  const context = useContext(VideoCallSidebarContext);

  if (context === null) {
    throw new Error('"VideoCallSidebarProvider" wrapper is not found');
  }
  return context;
};


export type VideoCallSidebarItem = {
  id: string;
  tooltipTitle: string;
  renderContent: (onClose: () => void) => React.ReactNode;
  icon: React.ReactNode;
  contentWidth?: number;
  // If true the component will not unmount when de-selected ( this is useful for keeping state )
  keepMounted?: boolean;
  disabled?: boolean;
};

export type VideoCallSidebarProps = {
  sidebarItems: VideoCallSidebarItem[];
};

const defaultDrawerWidth = 420;
const drawerTimeout = 400;

const StyledListItemButton = withStyles(ListItemButton, {
  root: {
    '&.Mui-selected': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.16)',
      },
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  }
});

export const VideoCallSidebar: React.VFC<VideoCallSidebarProps> = ({ sidebarItems }) => {
  const { classes } = useVideoCallLayoutStyles();
  const { activeItemId, setActiveItemId } = useVideoCallSidebarContext();
  const me = useCurrentuser();

  const onSidebarItemClick = (id: string, tooltipTitle: string) => {
    trackVideoSidebarButtonClick({
      buttonType: tooltipTitle,
      userType: me?.role,
      medicalRole: me?.medicalRole ?? ProviderMedicalRole.therapist
    });
    if (activeItemId) {
      setActiveItemId(null);
      if (activeItemId !== id) {
        setTimeout(() => setActiveItemId(id), drawerTimeout);
      }
    } else {
      setActiveItemId(id);
    }
  };

  const onCloseDrawer = () => setActiveItemId(null);
  const alreadyRenderedItemsRef = React.useRef<Record<string, boolean>>({});

  return (
    <Box className={classes.sidebarContainer} ml={activeItemId ? 2 : 0.5}>
      <UpliftThemeProvider>
        {sidebarItems.map(({ id, contentWidth, renderContent, keepMounted }) => {
          const isActive = id === activeItemId;
          const shouldRenderContent = (keepMounted && alreadyRenderedItemsRef.current[id]) || isActive;

          if (keepMounted && isActive) {
            alreadyRenderedItemsRef.current[id] = true;
          }

          return (
            <CollapseWidth
              key={id}
              in={isActive}
              timeout={drawerTimeout}
              width={`${contentWidth ?? defaultDrawerWidth}px`}
            >
              <Box
                className={classes.sidebarDrawer}
                width={`${contentWidth ?? defaultDrawerWidth}px`}
              >
                {shouldRenderContent && (
                  <Suspense fallback={null}>
                    {renderContent(onCloseDrawer)}
                  </Suspense>
                )}
              </Box>
            </CollapseWidth>
          );
        })}

      </UpliftThemeProvider>
      <List disablePadding>
        {sidebarItems.map(({ id, icon, tooltipTitle, disabled }) => {
          const isActive = id === activeItemId;

          return (
            <Tooltip
              key={id}
              disableHoverListener={disabled}
              aria-disabled={disabled}
              title={tooltipTitle}
              placement="left"
              className={classes.tooltip}
            >
              <StyledListItemButton
                disableGutters
                selected={isActive}
                className={classes.sidebarItem}
                disabled={disabled}
                onClick={disabled ? undefined : () => onSidebarItemClick(id, tooltipTitle)}
              >
                <IconButton
                  color="inherit"
                  disableRipple
                >
                  {icon}
                </IconButton>
              </StyledListItemButton>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );
};
